import { Component, QueryList, ViewChildren } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of as RxOf } from 'rxjs/observable/of';
import { defer as _defer, cloneDeep as _cloneDeep } from 'lodash';
import {
  AbstractBlModal,
  ModalReason,
  BlRadioGroupComponent
} from '@blcloud/bl-ng-ui-component';
import {
  BaseScreenActionSetting,
  CarSalesEstimatesSlipDivArray,
  CarSalesSlipOrderDivArray,
  CarSalesSlipTitleName,
  DisplayConfig,
  IBizMenuPageScreenActionSetting,
  PRODUCT_NAME,
  ReceptionBaseScreenActionSetting,
  ReceptionNavigationDivArray,
  RepairEstimatesSlipDivArray,
  RepairInstructionsSlipDivArray,
  RepairSalesSlipDivArray,
  SETTING_PATH,
  SfEstimatesSlipDivArray,
  SfInstructionsSlipDivArray,
  SfSalesSlipDivArray,
  SfSlipTitleName,
  SlipType
} from './biz-menu-page-screen-action-setting-modal.define';
import { ProductCode } from '@blcloud/bl-datamodel/enum/common/product-code';

@Component({
  templateUrl: './biz-menu-page-screen-action-setting-modal.component.html',
  styleUrls: ['./biz-menu-page-screen-action-setting-modal.component.scss']
})
export class BizMenuPageScreenActionSettingModalComponent extends AbstractBlModal {

  /** 画面動作設定 */
  screenActionSetting: IBizMenuPageScreenActionSetting;

  /** 各プロダクトの表示設定を格納する配列 */
  displayConfigs: DisplayConfig[] = [];

  /** 証跡帳票自動登録サービスの利用可否 */
  private isCaseEvidenceAutoRegistAvailable = false;

  // 受付行の表示対象は整備・鈑金（車販に受付なし）。
  private readonly receptionSettingProductCodes = [ProductCode.SuperFrontman, ProductCode.Repair];

  /** 最初の伝票ナビゲーションラジオグループコンポーネント */
  @ViewChildren('firstSlipNavigationDiv') private firstNavigationRadios: QueryList<BlRadioGroupComponent>;

  constructor(
  ) {
    super();
  }

  /**
   * @override
   */
  getResults(): any {
    return this.screenActionSetting;
  }

  /**
   * @override
   */
  onShown() {
    this.isCaseEvidenceAutoRegistAvailable =
      this.data && this.data.isCaseEvidenceAutoRegistAvailable === true;
    this.screenActionSetting = _cloneDeep(
      (this.data && this.data.screenActionSetting) || BaseScreenActionSetting
    );
    this.ensureReceptionSetting();
    // 表示設定の配列を生成する
    this.generateDisplayConfigs();

    // 初期フォーカス
    _defer(() => {
      // 最初の要素にフォーカス
      if (this.firstNavigationRadios && this.firstNavigationRadios.length > 0) {
        this.firstNavigationRadios.first.focus();
      }
    });
  }

  /**
   * 旧保存データに存在しない受付設定を既定値で補完する。
   */
  private ensureReceptionSetting(): void {
    // 保存値の shallow merge は維持し、旧レコードで欠落するトップレベルの受付設定だけを局所的に補完する。
    const savedReceptionSetting = this.screenActionSetting.reception;
    this.screenActionSetting.reception = {
      ..._cloneDeep(ReceptionBaseScreenActionSetting),
      ...(savedReceptionSetting || {})
    };
  }

  /**
   * @override
   */
  dismiss(reason: ModalReason) {
    if (reason === ModalReason.Done) {
      this.validate().subscribe(
        result => {
          if (result) {
            this.emitDismissEvent(reason);
          }
        }
      );
    } else {
      // 確定以外はそのまま通知
      this.emitDismissEvent(reason);
    }
  }

  /**
   * 入力検証します。
   * @returns 検証結果
   */
  private validate(): Observable<boolean> {
    return RxOf(true);
  }

  /**
   * 表示用のコンフィグレーションを生成する
   * プロダクトコードに応じて各伝票タイプの表示設定を作成し、displayConfigsに格納する
   */
  private generateDisplayConfigs(): void {
    const productCodeList = (this.data && this.data.productCodeList) || [];
    this.displayConfigs = productCodeList.map(productCode => {
      // プロダクトコードに応じて表示遷移する選択肢を設定する
      const navigationItems = this.setNavigationItems(productCode);
      // プロダクトコードに基づき、各伝票タイプの名称を設定する
      const firstSlipTypeName = this.getSlipTypeName(productCode, SlipType.First);
      const secondSlipTypeName = this.getSlipTypeName(productCode, SlipType.Second);
      // 車販はThirdがない
      const thirdSlipTypeName = (ProductCode.CarSales !== productCode)
        ? this.getSlipTypeName(productCode, SlipType.Third)
        : undefined;
      // 証跡管理オプション無効時は受付行を非表示。
      const showReceptionSetting = this.isReceptionSettingTarget(productCode) &&
        this.isCaseEvidenceAutoRegistAvailable;
      // 表示用のオブジェクトを作成
      const config: DisplayConfig = {
        // 各プロダクトコードに対応する名称
        productCodeName: this.getProductCodeName(productCode),
        // 各プロダクトコードに対応する詳細設定画面への経路
        settingPath: this.getSettingPath(productCode),
        // 一つ目の伝票遷移先項目リスト
        firstNavItems: navigationItems.firstSlipNavigationItems,
        // 二つ目の伝票遷移先項目リスト
        secondNavItems: navigationItems.secondSlipNavigationItems,
        // 三つ目の伝票遷移先項目リスト
        thirdNavItems: navigationItems.thirdSlipNavigationItems,
        // 一つ目の伝票種別名
        firstName: firstSlipTypeName,
        // 二つ目の伝票種別名
        secondName: secondSlipTypeName,
        // 三つ目の伝票種別名
        thirdName: thirdSlipTypeName,
        // 受付起動時の遷移先項目リスト
        receptionNavItems: showReceptionSetting ? this.getReceptionNavigationItems() : undefined,
        // 受付設定名
        receptionName: showReceptionSetting ? '受付' : undefined,
      };
      return config;
    });
  }

  /**
   * 受付設定を表示するプロダクトか判定する。
   * @param productCode プロダクトコード
   */
  private isReceptionSettingTarget(productCode: string): boolean {
    return this.receptionSettingProductCodes.some(target => target === productCode);
  }

  /**
   * 受付起動時の遷移先選択肢をラジオグループ用に変換する。
   */
  private getReceptionNavigationItems(): any[] {
    return ReceptionNavigationDivArray.map(item => ({ code: item.value, name: item.text }));
  }

  /**
   * プロダクトコードと伝票タイプに基づき、伝票名を取得します
   * @param productCode プロダクトコード
   * @param slipType 伝票タイプ
   * @returns 伝票名。該当しない場合は空文字を返します
   */
  private getSlipTypeName(productCode: string, slipType: SlipType): string {
    const titleNameGroup =
      (ProductCode.SuperFrontman === productCode || ProductCode.Repair === productCode)
        ? SfSlipTitleName
        : (ProductCode.CarSales === productCode)
          ? CarSalesSlipTitleName
          : null;

    // 対応するプロダクトコードでなく、タイトル名グループが取得できなかった場合
    if (!titleNameGroup) {
      return '';
    }

    // 取得したタイトル名グループから、slipTypeに応じた名前を取得
    switch (slipType) {
      case SlipType.First:
        return titleNameGroup.FirstName;
      case SlipType.Second:
        return titleNameGroup.SecondName;
      case SlipType.Third:
        // Thirdは車販以外にしか存在しないことを明確にする
        if (ProductCode.SuperFrontman === productCode || ProductCode.Repair === productCode) {
          return SfSlipTitleName.ThirdName;
        }
        // 車販の場合や、不明なプロダクトの場合は空文字を返す
        return '';
      default:
        return '';
    }
  }

  /**
   * プロダクトコードに基づき、各伝票タイプの選択肢リストを設定します
   * 未対応のプロダクトコードの場合は、空のリストを設定します
   * @param productCode プロダクトコード
   * @returns 各伝票タイプのナビゲーションアイテムリストを含むオブジェクト
   */
  private setNavigationItems(
    productCode: string
  ): {
    firstSlipNavigationItems: any[];
    secondSlipNavigationItems: any[];
    thirdSlipNavigationItems: any[];
  } {
    // 共通のmap処理を内部関数に定義してコードの重複を解消
    const mapToNavigationItems = (sourceArray: any[]) =>
      sourceArray.map(item => ({ code: item.value, name: item.text }));

    // 戻り値となるオブジェクトを初期化し、デフォルト値を設定します
    const result = {
      firstSlipNavigationItems: [],
      secondSlipNavigationItems: [],
      thirdSlipNavigationItems: []
    };
    switch (productCode) {
      // 整備
      case ProductCode.SuperFrontman: {
        result.firstSlipNavigationItems = mapToNavigationItems(SfEstimatesSlipDivArray);
        result.secondSlipNavigationItems = mapToNavigationItems(SfInstructionsSlipDivArray);
        result.thirdSlipNavigationItems = mapToNavigationItems(SfSalesSlipDivArray);
        break;
      }
      // 鈑金
      case ProductCode.Repair: {
        result.firstSlipNavigationItems = mapToNavigationItems(RepairEstimatesSlipDivArray);
        result.secondSlipNavigationItems = mapToNavigationItems(RepairInstructionsSlipDivArray);
        result.thirdSlipNavigationItems = mapToNavigationItems(RepairSalesSlipDivArray);
        break;
      }
      // 車販
      case ProductCode.CarSales: {
        result.firstSlipNavigationItems = mapToNavigationItems(CarSalesEstimatesSlipDivArray);
        result.secondSlipNavigationItems = mapToNavigationItems(CarSalesSlipOrderDivArray);
        // 車販はthirdがないので、デフォルトの空配列のままにする
        break;
      }
      default: {
        // 未知のプロダクトコードの場合：初期値を使用するため、ここでは何もしない
        break;
      }
    }
    return result;
  }

  /**
   * プロダクトコードに応じた、バインド先のプロパティキーを返すヘルパー関数
   * @param productCode - プロダクトコード
   * @returns プロダクトコードに対応するプロパティキー、該当しない場合は空文字を返します
   */
  private getSettingPath(productCode: string): string {
    switch (productCode) {
      // 整備
      case ProductCode.SuperFrontman:
        return SETTING_PATH.SF;
      // 鈑金
      case ProductCode.Repair:
        return SETTING_PATH.REPAIR;
      // 車販
      case ProductCode.CarSales:
        return SETTING_PATH.CAR_SALES;
      // 上記以外の場合
      default:
        return '';
    }
  }

  /**
   * プロダクトコード名称（例: '(整備)'）を取得します
   * @param productCode - プロダクトコード
   * @returns プロダクトコード名称。該当しない場合は空文字を返します
   */
  private getProductCodeName(productCode: string): string {
    switch (productCode) {
      // 整備
      case ProductCode.SuperFrontman:
        return PRODUCT_NAME.SUPERFRONTMAN;
      // 鈑金
      case ProductCode.Repair:
        return PRODUCT_NAME.REPAIR;
      // 車販
      case ProductCode.CarSales:
        return PRODUCT_NAME.CARSALES;
      // 上記以外の場合
      default:
        return '';
    }
  }

  /**
   * 一つ目の伝票のナビゲーションDIV値を取得します
   * 設定オブジェクトのパスを元に、screenActionSettingから値を安全に参照します
   * @param config - 表示設定オブジェクト。settingPathプロパティを含む必要があります
   * @returns 設定された一つ目のスリップのナビゲーションDIV値。存在しない場合はundefinedを返します
   */
  getFirstSlipValue(config: DisplayConfig) {
    const path = config.settingPath;
    // 指定されたパスの値を安全に取得するため、各階層で有効性を確認します
    const firstSlipValue = this.screenActionSetting
      && this.screenActionSetting[path]
      && this.screenActionSetting[path].firstSlipNavigationDiv;

    return firstSlipValue;
  }

  /**
   * 二つ目の伝票のナビゲーションDIV値を取得します
   * 設定オブジェクトのパスを元に、screenActionSettingから値を安全に参照します
   * @param config - 表示設定オブジェクト。settingPathプロパティを含む必要があります
   * @returns 設定された最初のスリップのナビゲーションDIV値。存在しない場合はundefinedを返します
   */
  getSecondSlipValue(config: DisplayConfig) {
    const path = config.settingPath;
    // 指定されたパスの値を安全に取得するため、各階層で有効性を確認します
    const secondSlipValue = this.screenActionSetting
      && this.screenActionSetting[path]
      && this.screenActionSetting[path].secondSlipNavigationDiv;

    return secondSlipValue;
  }

  /**
   * 三つ目の伝票のナビゲーションDIV値を取得します
   * 設定オブジェクトのパスを元に、screenActionSettingから値を安全に参照します
   * @param config - 表示設定オブジェクト。settingPathプロパティを含む必要があります
   * @returns 設定された三番目のスリップのナビゲーションDIV値。存在しない場合はundefinedを返します
   */
  getThirdSlipValue(config: DisplayConfig) {
    const path = config.settingPath;
    // 指定されたパスの値を安全に取得するため、各階層で有効性を確認します
    const thirdSlipValue = this.screenActionSetting
      && this.screenActionSetting[path]
      && this.screenActionSetting[path].thirdSlipNavigationDiv;

    return thirdSlipValue;
  }

  /**
   * 受付起動時のナビゲーションDIV値を取得する。
   * @param config 表示設定オブジェクト
   */
  getReceptionValue(config: DisplayConfig): string {
    const path = config && config.settingPath;
    return this.screenActionSetting
      && this.screenActionSetting.reception
      && this.screenActionSetting.reception[path];
  }

  /**
   * 一つ目の伝票のナビゲーションDIV値を設定します
   * 指定されたパスにオブジェクトが存在しない場合は、新規にオブジェクトを作成してから値を設定します
   * @param config - 設定オブジェクト。settingPathプロパティを含む必要があります
   * @param newValue - 設定する新しい値
   */
  setFirstSlipValue(config: DisplayConfig, newValue: string): void {
     // 設定オブジェクトまたはパスが無効な場合、処理を中断する
    if (!config || !config.settingPath || !this.screenActionSetting) {
      return;
    }

    const path = config.settingPath;
    // screenActionSetting[path] が falsy (null, undefined, 0, ''など) の場合、
    // 代わりに新しい空オブジェクト {} が targetObject に代入されます
    // 既存のオブジェクトがある場合は、そのオブジェクトがそのまま使われます
    const targetObject = this.screenActionSetting[path] || {};
    // もし path の値が falsy だった場合、新しいオブジェクトを
    // screenActionSetting[path] に設定し直して、データを保持します
    this.screenActionSetting[path] = targetObject;
    // 最終的に、対象オブジェクトに値を設定します。
    targetObject.firstSlipNavigationDiv = newValue;
  }

  /**
   * 二つ目の伝票のナビゲーションDIV値を設定します
   * @param config - 設定オブジェクト。settingPathプロパティを含む必要があります
   * @param newValue - 設定する新しい値
   */
  setSecondSlipValue(config: DisplayConfig, newValue: string): void {
    // 設定オブジェクトまたはパスが無効な場合、処理を中断する
    if (!config || !config.settingPath || !this.screenActionSetting) {
      return;
    }

    const path = config.settingPath;
    // screenActionSetting[path] が falsy (null, undefined, 0, ''など) の場合、
    // 代わりに新しい空オブジェクト {} が targetObject に代入されます
    // 既存のオブジェクトがある場合は、そのオブジェクトがそのまま使われます
    const targetObject = this.screenActionSetting[path] || {};
    // もし path の値が falsy だった場合、新しいオブジェクトを
    // screenActionSetting[path] に設定し直して、データを保持します。
    this.screenActionSetting[path] = targetObject;
    // 最終的に、対象オブジェクトに値を設定します。
    targetObject.secondSlipNavigationDiv = newValue;
  }

  /**
   * 三つ目の伝票のナビゲーションDIV値を設定します
   * @param config - 設定オブジェクト。settingPathプロパティを含む必要があります
   * @param newValue - 設定する新しい値
   */
  setThirdSlipValue(config: DisplayConfig, newValue: string): void {
    // 設定オブジェクトまたはパスが無効な場合、処理を中断する
    if (!config || !config.settingPath || !this.screenActionSetting) {
      return;
    }

    const path = config.settingPath;
    // screenActionSetting[path] が falsy (null, undefined, 0, ''など) の場合、
    // 代わりに新しい空オブジェクト {} が targetObject に代入されます
    // 既存のオブジェクトがある場合は、そのオブジェクトがそのまま使われます
    const targetObject = this.screenActionSetting[path] || {};
    // もし path の値が falsy だった場合、新しいオブジェクトを
    // screenActionSetting[path] に設定し直して、データを保持します。
    this.screenActionSetting[path] = targetObject;
    // 最終的に、対象オブジェクトに値を設定します。
    targetObject.thirdSlipNavigationDiv = newValue;
  }

  /**
   * 受付起動時のナビゲーションDIV値を設定する。
   * @param config 表示設定オブジェクト
   * @param newValue 設定する新しい値
   */
  setReceptionValue(config: DisplayConfig, newValue: string): void {
    if (!config || !config.settingPath || !this.screenActionSetting || !this.screenActionSetting.reception) {
      return;
    }
    this.screenActionSetting.reception[config.settingPath] = newValue;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/biz-menu-page-screen-action-setting-modal/biz-menu-page-screen-action-setting-modal.component.ts